import FontIcon from 'components/icons/FontIcon';
import { TextButton } from 'components/v2/buttons';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { HelpBox } from 'components/v2/help-boxes';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { useEffect, useState } from 'react';
import { MaterialPropertyModel } from 'services/EpdClient';
import {
  useCreateProductMaterialProperty,
  useDeleteProductMaterialProperty,
  useUpdateProductMaterialProperty,
} from 'services/api/mutations';
import { useMaterialPropertiesLookup } from 'services/api/queries';
import {
  DoubleFieldGrid,
  ExpandablePanelHeaderName,
  ExpandablePanelModule,
  HeaderCaptionSemiBold,
} from 'styles/v2/Styles.styled';
import { EPDProductInformationModel, EPDProductMaterialPropertyModel, Option } from 'types/types';
import { getValidationIssueMessage } from 'util/compilerValidation';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import ToggleButton from '../../panels/toggleable/ToggleButton';
import { TooltipPosition } from 'components/controls/Tooltip';
import { ErrorAlertPanel } from '../../panels/Styles.styled';
import { toaster } from 'components/v2/toast';
import { useTranslation } from 'react-i18next';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';

const MaterialPropertiesSection: React.FunctionComponent<{
  product?: EPDProductInformationModel;
  onChange: any;
  errors: any;
}> = ({ product, errors }) => {
  const { t } = useTranslation();
  const isReadOnly = useIsReadOnlyMode();
  const materialPropertiesLookup = useMaterialPropertiesLookup().data;

  const [rowValues, setRowValues] = useState(product?.productMaterialProperties ?? []);

  useEffect(() => {
    setRowValues(product?.productMaterialProperties ?? []);
  }, [product?.productMaterialProperties]);

  const createMutation = useCreateProductMaterialProperty();
  const deleteMutation = useDeleteProductMaterialProperty();
  const updateMutation = useUpdateProductMaterialProperty();

  function noErrorsAtProductMaterialPropertiesBlock(errors: any): boolean | undefined {
    const fieldNames: string[] = ['materialPropertiesBlock'];

    return !errors || !fieldNames.some((field) => field in errors);
  }

  const materialPropertyErrors = (errors: any, index: any) => {
    return errors?.materialPropertiesBlock?.[`productMaterialProperties[${index}]`];
  };

  const confirmDeleteMaterialProp = (materialProperty: EPDProductMaterialPropertyModel) => {
    confirmDelete({
      caption: 'Are you sure you want to delete material property?',
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(materialProperty?.id as string);
        } catch (e) {
          toaster({
            severity: 'error',
            summary: 'Error',
            details: t('epdWizard.messages.error') as string,
          });
        }
      },
    });
  };

  const createRow = async () => {
    try {
      await createMutation.mutateAsync(product?.id!);
    } catch (e) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: t('epdWizard.messages.error') as string,
      });
    }
  };

  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const onChangeInternal = async (
    item: EPDProductMaterialPropertyModel,
    name: keyof EPDProductMaterialPropertyModel,
    value: any
  ) => {
    if (!item.id) return;
    if ((item as any)[name] === value) return;

    const next: EPDProductMaterialPropertyModel = { ...item, [name]: value };
    setUpdatingId(item.id);
    try {
      await updateMutation.mutateAsync(next);
    } catch (e) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: t('epdWizard.messages.error') as string,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const selectedMaterialProp = (item: EPDProductMaterialPropertyModel) => {
    if (item?.materialPropertyId) {
      return {
        value: item.materialPropertyId,
        label: materialPropertiesLookup?.find((prop: any) => prop.id === item.materialPropertyId)?.fullName,
      } as Option;
    }
    return null;
  };

  const headerModuleTemplate = (
    options: any,
    header: string,
    tooltip?: string,
    tooltipPosition?: TooltipPosition,
    required?: boolean,
    isFilledIn?: boolean
  ) => {
    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HeaderCaptionSemiBold>{header}</HeaderCaptionSemiBold>
          {tooltip && <TooltipHelpIcon content={tooltip} position={tooltipPosition} />}
          {required && !isFilledIn && <TooltipErrorIcon />}
          <ToggleButton options={options} />
        </ExpandablePanelHeaderName>
      </div>
    );
  };

  return (
    <ExpandablePanelModule
      headerTemplate={(options) =>
        headerModuleTemplate(
          options,
          'Material properties',
          'Declaration of relevant non-scaling physical or chemical product properties',
          'right',
          true,
          noErrorsAtProductMaterialPropertiesBlock(errors)
        )
      }
      toggleable
    >
      {errors?.materialPropertiesBlock?.flowDependencyError && (
        <ErrorAlertPanel className="w-full">
          {getValidationIssueMessage(errors?.materialPropertiesBlock?.flowDependencyError)}
        </ErrorAlertPanel>
      )}

      {rowValues?.map((item, index) => (
        <DoubleFieldGrid key={item.id}>
          <CompilerSelectControlledComponent
            key={'Unit' + item.id}
            label="Unit"
            name="materialPropertyId"
            required
            options={(materialPropertiesLookup || [])
              .filter((x) => {
                if (x.fullName?.startsWith('Conversion factor to mass')) return false;
                if (x.fullName?.startsWith('Grammage (kg') || x.fullName?.startsWith('Gross density (kg')) {
                  return false;
                }

                return true;
              })
              .map((x: MaterialPropertyModel) => {
                return {
                  value: x.id,
                  label: x.fullName,
                  isDisabled: rowValues.some((mp) => mp.materialPropertyId === x.id),
                } as Option;
              })
              .filter((option) => !rowValues?.find((rowValue: any) => rowValue.id === option.value))}
            value={selectedMaterialProp(item)}
            onChanged={(name: any, value: any) => {
              onChangeInternal(item, name, value);
            }}
            placeholder="Select"
            error={materialPropertyErrors(errors, index)?.unit}
            disabled={updatingId === item.id || !item.id}
            {...reactSelectPropsForHtmlLabel}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
            <WizardTextInputComponent
              key={'Value' + item.id}
              label="Value"
              placeholder="Type here"
              required
              value={item?.value === 0 ? '0' : item?.value ?? ''}
              onChanged={(name: any, value: any) => {
                onChangeInternal(item, name, value);
              }}
              name="value"
              error={materialPropertyErrors(errors, index)?.value}
              disabled={updatingId === item.id || !item.id}
            />
            {!isReadOnly && index !== 0 && (
              <TextButton
                style={{ alignSelf: 'flex-end', margin: '0.25rem' }}
                type="button"
                text
                icon={<FontIcon icon="delete" />}
                onClick={() => confirmDeleteMaterialProp(item)}
              />
            )}
          </div>
        </DoubleFieldGrid>
      ))}
      <div>
        <HelpBox>
          <span>You can add more material properties if the product have more than one.</span>
        </HelpBox>
        <WizardAddButton label="+ Add material property" onClick={createRow} />
      </div>
    </ExpandablePanelModule>
  );
};

export default MaterialPropertiesSection;
